/**
 * Brand context. The current brand is the `:brand` route param (a brand id). Screens without a
 * brand in the URL (/, /research, /explorer, /settings) keep the last one picked, which is saved
 * in localStorage `seohq.brand`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { useSb, type SbError } from './query';
import { useMember, type Role } from './auth';
import { brandIdFromPath, findPage, pathFor } from './nav';
import type { Tables } from './database.types';

export type Brand = Tables<'brands'>;

export type BrandState = {
  /** Brands this user can read, by name. Empty while loading. */
  brands: Brand[];
  /** The current brand: from the URL on brand screens, else the last one picked. */
  brand: Brand | null;
  /** Switches brand. On a brand screen it moves to the same screen for the new brand. */
  setBrandId: (id: string) => void;
  /** The user's role on the current brand. Owners are admin everywhere. */
  role: Role | null;
  /** The user's role on any brand (for lists that span brands). */
  roleFor: (brandId: string) => Role | null;
  isOwner: boolean;
  /** True until the brands and the role rows have loaded. */
  loading: boolean;
  /** The `:brand` id in the URL, if any (may not match a readable brand). */
  routeBrandId: string | null;
  /** Set when the brand list failed to load. */
  brandsError: SbError | null;
};

const STORAGE_KEY = 'seohq.brand';
const BrandContext = createContext<BrandState | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
function writeStored(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* storage blocked */
  }
}

/** Curated accents, [light, dark]: cobalt, pine, plum, ochre, teal, rose. */
const ACCENTS: [string, string][] = [
  ['#2f55c8', '#7f9cf0'],
  ['#1d7454', '#5fc79c'],
  ['#7a3d9e', '#c294e0'],
  ['#a5700e', '#e3b453'],
  ['#0f7486', '#5cc3d4'],
  ['#b0365e', '#ec8aa8'],
];

/** The brand's own color when set (a #hex), else a stable pick from ACCENTS by brand id. */
export function brandAccent(id: string, color: string | null): [string, string] {
  if (color && /^#[0-9a-f]{6}$/i.test(color)) return [color, `color-mix(in oklab, ${color} 62%, white)`];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const member = useMember().data ?? null;
  const location = useLocation();
  const navigate = useNavigate();
  const routeBrandId = brandIdFromPath(location.pathname);
  const [storedId, setStoredId] = useState<string | null>(() => readStored());

  const brandsQ = useSb(['seo', 'brands', 'list'], () => supabase.from('brands').select('*').order('name'), {
    enabled: !!member,
  });
  const rolesQ = useSb(
    ['seo', 'brand_members', 'mine', member?.id],
    () => supabase.from('brand_members').select('brand_id, role').eq('member_id', member!.id),
    { enabled: !!member && !member.is_owner },
  );

  const brands = useMemo(() => brandsQ.data ?? [], [brandsQ.data]);
  const roleMap = useMemo(() => new Map((rolesQ.data ?? []).map((r) => [r.brand_id, r.role as Role])), [rolesQ.data]);
  const isOwner = !!member?.is_owner;

  const brand = useMemo(() => {
    if (routeBrandId) return brands.find((b) => b.id === routeBrandId) ?? null;
    return brands.find((b) => b.id === storedId) ?? brands[0] ?? null;
  }, [brands, routeBrandId, storedId]);

  // Each brand carries its own accent (top ribbon, active nav marker, sparklines).
  useEffect(() => {
    const root = document.documentElement;
    if (!brand) {
      root.style.removeProperty('--accent-l');
      root.style.removeProperty('--accent-d');
      return;
    }
    const [l, d] = brandAccent(brand.id, (brand as { color?: string | null }).color ?? null);
    root.style.setProperty('--accent-l', l);
    root.style.setProperty('--accent-d', d);
  }, [brand]);

  // A brand in the URL becomes the remembered one.
  useEffect(() => {
    if (routeBrandId && brands.some((b) => b.id === routeBrandId) && routeBrandId !== storedId) {
      writeStored(routeBrandId);
      setStoredId(routeBrandId);
    }
  }, [routeBrandId, brands, storedId]);

  const roleFor = useCallback(
    (brandId: string): Role | null => (isOwner ? 'admin' : (roleMap.get(brandId) ?? null)),
    [isOwner, roleMap],
  );

  const setBrandId = useCallback(
    (id: string) => {
      writeStored(id);
      setStoredId(id);
      const page = findPage(location.pathname);
      if (page?.brand) {
        // Same screen, new brand; keep any deeper path after the screen's own path.
        const rest = location.pathname.slice(pathFor(page.key, routeBrandId ?? id).length);
        navigate(pathFor(page.key, id) + rest + location.search);
      }
    },
    [location.pathname, location.search, navigate, routeBrandId],
  );

  const value = useMemo<BrandState>(
    () => ({
      brands,
      brand,
      setBrandId,
      role: brand ? roleFor(brand.id) : null,
      roleFor,
      isOwner,
      loading: brandsQ.isPending || (!isOwner && !!member && rolesQ.isPending),
      routeBrandId,
      brandsError: brandsQ.error ?? rolesQ.error ?? null,
    }),
    [brands, brand, setBrandId, roleFor, isOwner, brandsQ.isPending, rolesQ.isPending, member, routeBrandId, brandsQ.error, rolesQ.error],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandState {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used inside <BrandProvider>');
  return ctx;
}

/**
 * For screens that always have a brand (`/b/:brand/...`): the brand and role, never null.
 * Only call it inside a page rendered by the router, where BrandRoute has already checked
 * that the brand exists.
 */
export function useCurrentBrand(): { brand: Brand; role: Role | null } {
  const { brand, role } = useBrand();
  if (!brand) throw new Error('useCurrentBrand used outside a brand route');
  return { brand, role };
}
